import React, { useState, useMemo, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  IconButton, 
  Paper, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  Grid,
  Tooltip
} from '@mui/material';
import { 
  Home as HomeIcon, 
  Edit, 
  Delete, 
  Search, 
  ChevronRight,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { getTelegramTranslations, updateTelegramTranslation, getNewIdString, insertTelegramTranslation, deleteTelegramTranslation } from '../services/translationService';

function TelegramTranslation() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [originalTranslation, setOriginalTranslation] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  
  // Paginazione
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Effetto per il controllo iniziale delle traduzioni
  useEffect(() => {
    const checkAndFetchTranslations = async () => {
      try {
        const count = await db.strings.count();
        const lastUpdateEntry = await db.genericTable.get({ table: 'settings', field: 'lastupdatestringtelegram' });
        
        if (count === 0) {
          // Nessuna stringa presente, scarica tutto senza parametri
          await getTelegramTranslations();
          setToast({ open: true, message: 'Traduzioni caricate per la prima volta', severity: 'info' });
        } else if (lastUpdateEntry) {
          // Controlla se è passato più di un giorno
          const lastUpdateDate = new Date(lastUpdateEntry.value);
          const now = new Date();
          const oneDayInMs = 24 * 60 * 60 * 1000;
          
          if (now - lastUpdateDate > oneDayInMs) {
            // Passato più di un giorno, aggiorna con lastupdate
            await getTelegramTranslations(lastUpdateEntry.value);
            setToast({ open: true, message: 'Traduzioni aggiornate (ultimo sincronismo > 24h)', severity: 'info' });
          }
        } else {
          // Stringhe presenti ma manca il log dell'ultimo aggiornamento, scarichiamo tutto per allineare
          await getTelegramTranslations();
        }
      } catch (error) {
        console.error('Errore nel caricamento iniziale delle traduzioni:', error);
      }
    };

    checkAndFetchTranslations();
  }, []);

  // Ottieni le traduzioni da Dexie
  const translations = useLiveQuery(() => db.strings.toArray());

  // Reset pagina alla ricerca
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // Filtraggio traduzioni
  const filteredTranslations = useMemo(() => {
    const data = translations || [];
    return data.filter(t => {
      const stringId = String(t.string || '');
      const description = t.description || '';
      return stringId.toLowerCase().includes(searchQuery.toLowerCase()) || 
             description.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [translations, searchQuery]);

  // Sottoset paginato
  const paginatedTranslations = useMemo(() => {
    return filteredTranslations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredTranslations, page, rowsPerPage]);

  if (!translations) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
        <Typography sx={{ mt: 2 }}>Caricamento traduzioni...</Typography>
      </Box>
    );
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = async (translation = null) => {
    const isNew = !translation;
    
    // Inizializza i dati (vuoti o esistenti)
    const initialData = translation || { 
      string: '', 
      language: '', 
      description: '', 
      date: new Date().toISOString().slice(0, 16), 
      user: 0, 
      bot: 0 
    };

    setSelectedTranslation({...initialData});
    setOriginalTranslation(translation ? {...initialData} : null);
    setIsModalOpen(true);

    // Se è una nuova traduzione, recupera l'ID e l'utente
    if (isNew) {
      try {
        // Recupera user_id da Dexie per precompilare il form
        const userProfile = await db.genericTable.get({ field: 'user', table: 'userProfile' });
        const currentUserId = userProfile ? userProfile.value : 0;

        const result = await getNewIdString();
        
        setSelectedTranslation(prev => ({
          ...prev,
          string: (result && result.result && result.details) ? result.details : prev.string,
          user: currentUserId
        }));
      } catch (error) {
        console.error('Errore nel recupero dei dati iniziali:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTranslation(null);
    setOriginalTranslation(null);
  };

  const handleSaveTranslation = async () => {
    try {
      if (originalTranslation) {
        // MODIFICA
        const values = [];
        const fieldsToSync = ['language', 'description', 'user', 'bot'];

        fieldsToSync.forEach(field => {
          let newValue = selectedTranslation[field];
          let oldValue = originalTranslation[field];

          // Cast per numeri
          if (field === 'user' || field === 'bot') {
            newValue = parseInt(newValue) || 0;
            oldValue = parseInt(oldValue) || 0;
          }

          if (newValue !== oldValue) {
            values.push({ field: field, value: newValue });
          }
        });

        // Se nulla è cambiato, chiudiamo
        if (values.length === 0) {
          handleCloseModal();
          return;
        }

        // Chiamata API per aggiornamento
        const apiResult = await updateTelegramTranslation(
          originalTranslation.string, 
          originalTranslation.language, 
          values
        );

        if (!apiResult || !apiResult.result) {
          throw new Error(apiResult?.text || 'Errore API durante l\'aggiornamento');
        }
      } else {
        // INSERIMENTO NUOVO
        const dataToInsert = {
          ...selectedTranslation,
          user: parseInt(selectedTranslation.user) || 0,
          date: new Date().toISOString()
        };

        const apiResult = await insertTelegramTranslation(dataToInsert);
        if (!apiResult || !apiResult.result) {
          throw new Error(apiResult?.text || 'Errore API durante l\'inserimento');
        }

        // Aggiorna la data con il timestamp attuale dopo la conferma
        selectedTranslation.date = new Date().toISOString();
      }

      // In ogni caso aggiorniamo Dexie localmente
      const dataToSave = {
        ...selectedTranslation,
        string: originalTranslation ? originalTranslation.string : selectedTranslation.string,
        user: parseInt(selectedTranslation.user) || 0,
        bot: parseInt(selectedTranslation.bot) || 0,
        date: selectedTranslation.date
      };

      // Se la lingua è cambiata in modifica, dobbiamo eliminare la vecchia chiave primaria da Dexie
      if (originalTranslation && originalTranslation.language !== selectedTranslation.language) {
        await db.strings.where({ string: originalTranslation.string, language: originalTranslation.language }).delete();
      }

      await db.strings.put(dataToSave);
      
      setToast({ open: true, message: 'Traduzione salvata con successo', severity: 'success' });
      handleCloseModal();
    } catch (error) {
      console.error(error);
      setToast({ open: true, message: 'Errore: ' + error.message, severity: 'error' });
    }
  };

  const handleDeleteTranslation = async (string, language) => {
    if (window.confirm('Sei sicuro di voler eliminare questa traduzione?')) {
      try {
        const result = await deleteTelegramTranslation(string, language);
        if (result && result.result) {
          await db.strings.where({ string, language }).delete();
          setToast({ open: true, message: 'Traduzione eliminata', severity: 'info' });
        } else {
          throw new Error(result?.text || 'Errore durante l\'eliminazione lato server');
        }
      } catch (error) {
        console.error('Errore eliminazione:', error);
        setToast({ open: true, message: 'Errore: ' + error.message, severity: 'error' });
      }
    }
  };

  const handleRefresh = async () => {
    try {
      const lastUpdateEntry = await db.genericTable.get({ table: 'settings', field: 'lastupdatestringtelegram' });
      await getTelegramTranslations(lastUpdateEntry?.value);
      setToast({ open: true, message: 'Traduzioni aggiornate', severity: 'success' });
    } catch (error) {
      setToast({ open: true, message: 'Errore durante l\'aggiornamento', severity: 'error' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>Traduzioni Telegram</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Aggiorna traduzioni">
            <IconButton onClick={handleRefresh} color="primary" sx={{ border: '1px solid rgba(255,255,255,0.1)' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenModal()}
          >
            Aggiungi Traduzione
          </Button>
        </Box>
      </Box>

      {/* Search Header */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Cerca per ID stringa o descrizione..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'rgba(255,255,255,0.5)' }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Translations Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <TableRow>
              <TableCell>String (ID)</TableCell>
              <TableCell>Lingua</TableCell>
              <TableCell>Descrizione</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="center">User</TableCell>
              <TableCell align="center">Bot</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTranslations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, opacity: 0.5 }}>
                  Nessuna traduzione trovata.
                </TableCell>
              </TableRow>
            ) : (
              paginatedTranslations.map((t) => (
                <TableRow 
                  key={`${t.string}-${t.language}`} 
                  hover 
                  onClick={() => handleOpenModal(t)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {t.string}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                      {t.language}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap>
                      {t.description}
                    </Typography>
                  </TableCell>
                  <TableCell variant="caption">
                    {t.date ? new Date(t.date).toLocaleString() : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {t.user || 0}
                  </TableCell>
                  <TableCell align="center">
                    {t.bot || 0}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(t);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTranslation(t.string, t.language);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredTranslations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
          labelRowsPerPage="Righe per pagina:"
        />
      </TableContainer>

      {/* Modal Modifica/Aggiunta */}
      <Dialog 
        fullScreen 
        open={isModalOpen} 
        onClose={handleCloseModal}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'rgba(255,255,255,0.05)',
          px: 4,
          py: 2
        }}>
          <Typography variant="h6">
            {selectedTranslation?.string ? `Modifica Traduzione: ${selectedTranslation.string}` : 'Nuova Traduzione'}
          </Typography>
          <IconButton onClick={handleCloseModal} color="inherit">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {/* Spazio tra header e form */}
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="String (ID)"
                  fullWidth
                  type="number"
                  disabled={!!originalTranslation}
                  value={selectedTranslation?.string || ''}
                  onChange={(e) => setSelectedTranslation({ ...selectedTranslation, string: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Language (2 caratteri)"
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  value={selectedTranslation?.language || ''}
                  onChange={(e) => setSelectedTranslation({ ...selectedTranslation, language: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={selectedTranslation?.description || ''}
                  onChange={(e) => setSelectedTranslation({ ...selectedTranslation, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Date"
                  fullWidth
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  disabled
                  value={selectedTranslation?.date || ''}
                  onChange={(e) => setSelectedTranslation({ ...selectedTranslation, date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="User (ID)"
                  fullWidth
                  type="number"
                  value={selectedTranslation?.user || 0}
                  onChange={(e) => setSelectedTranslation({ ...selectedTranslation, user: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Bot (ID)"
                  fullWidth
                  type="number"
                  value={selectedTranslation?.bot || 0}
                  onChange={(e) => setSelectedTranslation({ ...selectedTranslation, bot: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 4, backgroundColor: 'rgba(255,255,255,0.02)' }}>
          <Button onClick={handleCloseModal} size="large">Annulla</Button>
          <Button variant="contained" onClick={handleSaveTranslation} size="large">Salva Traduzione</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TelegramTranslation;
